import { useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  DocumentChartBarIcon,
  CpuChipIcon,
  GlobeAltIcon,
  LockClosedIcon,
  ArrowRightIcon,
  BeakerIcon,
  SparklesIcon,
  ChartBarIcon,
  CalendarIcon,
  UserGroupIcon,
  TrophyIcon,
  ClockIcon,
  MapIcon,
  Squares2X2Icon,
  Bars3Icon,
  XMarkIcon,
  UserPlusIcon,
  BanknotesIcon,
  ServerStackIcon,
  SunIcon,
  BuildingLibraryIcon,
  ScaleIcon,
  BuildingOffice2Icon,
  TableCellsIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { FadeIn, StaggeredFadeIn } from '../components/animations/FadeIn';
import { Typewriter, TypewriterSequence } from '../components/animations/Typewriter';
import { PRODUCTS_CATALOG } from '../types/products';
import { PolygonIcon } from '../components/icons/PolygonIcon';
import { PhreaticIcon } from '../components/icons/PhreaticIcon';
import { PulseWaveIcon } from '../components/icons/PulseWaveIcon';
import { PathfinderIcon } from '../components/icons/PathfinderIcon';
import { TopographicBackground } from '../components/landing/TopographicBackground';

const coreCapabilities = [
  {
    icon: CpuChipIcon,
    title: 'PHYSICS-INFORMED MODELS',
    description:
      'Physics-informed machine learning across forecasting, classification, and document extraction. Every product runs on models trained on real environmental data — not synthetic benchmarks.',
  },
  {
    icon: CalendarIcon,
    title: 'MULTI-HORIZON FORECASTING',
    description:
      'Predictions delivered at multiple time horizons, from today to two decades out. Plan capital allocation against the actual trajectory of a site, not just its current state.',
  },
  {
    icon: BeakerIcon,
    title: 'AQUIFER-AWARE MODELING',
    description:
      'Forecasts routed to the right model per well. The platform knows the difference between major aquifers and treats each on its own physics — no cross-aquifer averaging.',
  },
  {
    icon: ClockIcon,
    title: 'DECADES OF FIELD DATA',
    description:
      'Trained on 25+ years of real Talon/LPE site work. Every algorithm is informed by thousands of actual contaminated sites and water-availability assessments.',
  },
  {
    icon: ChartBarIcon,
    title: 'PORTFOLIO INTELLIGENCE',
    description:
      'Manage hundreds of sites with automated prioritization. Trajectory drives ranking — not just current state — so the sites that need attention surface first.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'REGULATORY-AWARE',
    description:
      'Federal and state thresholds flow through plumes, well placement, document tagging, and lending recommendations. Switch jurisdictions and every downstream calculation re-renders.',
  },
];

const industries = [
  {
    icon: BeakerIcon,
    name: 'PETROLEUM',
    description: 'UST release sites, bulk terminals, refineries, and BTEX plume management.',
  },
  {
    icon: BanknotesIcon,
    name: 'BANKING & FINANCE',
    description: 'Groundwater availability certification for agricultural lending, property due diligence, and portfolio risk assessment.',
  },
  {
    icon: ServerStackIcon,
    name: 'DATA CENTERS',
    description: 'Site selection water availability analysis, cooling water sustainability, and long-term aquifer capacity verification.',
  },
  {
    icon: SunIcon,
    name: 'AGRICULTURE',
    description: 'Irrigation sustainability, aquifer drawdown projections, water rights valuation, and feedyard environmental compliance.',
  },
  {
    icon: BuildingLibraryIcon,
    name: 'PUBLIC SECTOR',
    description: 'Municipal water utilities, state environmental and water-development agencies, and public land and infrastructure planning.',
  },
  {
    icon: ScaleIcon,
    name: 'LAW FIRMS',
    description: 'Environmental litigation support, contamination liability casework, and regulatory compliance defense backed by defensible forecasts.',
  },
  {
    icon: BuildingOffice2Icon,
    name: 'DEVELOPERS',
    description: 'Land acquisition due diligence, water availability for master-planned communities, and contamination risk screening on redevelopment parcels.',
  },
];

const productIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  PolygonIcon,
  PhreaticIcon,
  TableCellsIcon,
  ShieldCheckIcon,
  DocumentChartBarIcon,
  PulseWaveIcon,
  PathfinderIcon,
};

const productAccents: Record<string, string> = {
  cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  violet: 'text-violet-400 bg-violet-500/10 border-violet-500/30',
};


export function Landing() {
  const FEATURED_SLUGS = ['polygon', 'phreatic', 'datavault'];
  const featuredProducts = FEATURED_SLUGS
    .map(slug => PRODUCTS_CATALOG.find(p => p.slug === slug))
    .filter((p): p is NonNullable<typeof p> => p !== undefined);
  const [menuOpen, setMenuOpen] = useState(false);
  const [heroDone, setHeroDone] = useState(false);
  const onHeroComplete = useCallback(() => setHeroDone(true), []);
  return (
    <div className="min-h-screen bg-black relative">
      {/* Layer 1: Animated topographic — fixed, sits behind the hero. Logo background
          scrolls in from below and covers it for the post-hero sections. */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <TopographicBackground className="w-full h-full" spill />
        {/* Left-to-right black fade so the hero title sits on dark and animation breathes on the right */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent pointer-events-none" />
      </div>
      {/* Header - Sticky on scroll, containerized + transparent like sections */}
      <header className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="pointer-events-auto bg-white/80 backdrop-blur-md border border-gray-400/40 rounded-lg">
            <div className="px-6 py-3 flex items-center justify-between">
              <Link to="/" className="flex items-center gap-3">
                <img src="/argos-black-letters-2.png" alt="ARGOS" className="h-12 object-contain" />
              </Link>

              {/* Hamburger Menu Button */}
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2 text-gray-700 hover:bg-gray-100/60 rounded transition-colors"
              >
                {menuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </div>

        {/* Dropdown Menu */}
        {menuOpen && (
          <nav className="border-t border-gray-400/40">
            <div className="px-6 py-4 flex flex-col gap-2">
              <a
                href="#products"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-xs tracking-widest text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Squares2X2Icon className="h-5 w-5" />
                PRODUCTS
              </a>
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-xs tracking-widest text-gray-700 hover:text-cyan-500 hover:bg-cyan-50 transition-colors"
              >
                <EnvelopeIcon className="h-5 w-5" />
                CONTACT
              </Link>
              <Link
                to="/client/signup"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-xs tracking-widest text-gray-700 hover:text-cyan-500 hover:bg-cyan-50 transition-colors"
              >
                <UserPlusIcon className="h-5 w-5" />
                SIGNUP
              </Link>
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="flex items-center gap-3 px-4 py-3 text-xs tracking-widest text-gray-700 hover:text-cyan-500 hover:bg-cyan-50 transition-colors"
              >
                <ArrowRightIcon className="h-5 w-5" />
                LOGIN
              </Link>
            </div>
          </nav>
        )}
          </div>
        </div>
      </header>

      {/* Hero - content scrolls over fixed background */}
      <section className="relative z-10 border-b border-cyan-500/10 overflow-hidden min-h-screen flex items-center">
        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-8 w-full">
          <TypewriterSequence
            delay={200}
            gap={150}
            onComplete={onHeroComplete}
            items={[
              {
                text: 'ADVANCED GEO INTELLIGENCE SOLUTIONS',
                as: 'p',
                className: 'text-xs text-cyan-400 tracking-[0.4em] mb-5',
                speed: 25,
              },
              {
                text: 'AQUIFER INTELLIGENCE FOR GROUNDWATER AVAILABILITY & CONTAMINATION',
                as: 'h1',
                className: 'text-4xl lg:text-5xl font-bold text-white tracking-wide leading-tight max-w-5xl font-orbitron',
                speed: 30,
              },
            ]}
          />
          <FadeIn revealAfterMs={0} delay={900} duration={600}>
            <p className="mt-8 text-white/90 text-base tracking-wide max-w-3xl leading-relaxed">
              A suite of specialized tools for aquifer modeling, contamination forecasting, and
              environmental data management &mdash; built on decades of real field work.
            </p>
          </FadeIn>
          <FadeIn revealAfterMs={0} delay={1150} duration={600}>
            <Link
              to="/signup"
              className="mt-8 inline-flex items-center gap-2 px-8 py-3.5 rounded-lg border border-cyan-400/40 bg-cyan-500/10 backdrop-blur-md text-cyan-200 text-sm font-semibold tracking-[0.3em] font-orbitron shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-8px_rgba(34,211,238,0.5)] hover:bg-cyan-500/20 hover:border-cyan-400/70 hover:text-cyan-100 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_0_36px_-4px_rgba(34,211,238,0.7)] transition-all duration-300"
            >
              SIGN UP
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Sticky ARGOS logo background — scrolls IN from below the hero, then sticks at the
          top for the rest of the page. Sits behind all post-hero sections (z-[1]) but on
          top of the topographic layer (z-0). Negative -mb pulls subsequent sections up so
          they overlap and render on top of this. */}
      <div className="sticky top-0 h-screen z-[1] -mb-[100vh] overflow-hidden pointer-events-none bg-mono-700">
        <img
          src="/argos-full-black.png"
          alt=""
          aria-hidden="true"
          className="absolute top-[58%] left-[-5vw] -translate-y-1/2 h-[250vw] md:h-[400vh] w-auto max-w-none object-contain opacity-30 select-none [filter:drop-shadow(0_18px_40px_rgba(0,0,0,0.55))]"
        />
        {/* Overhead spotlight */}
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(ellipse_60%_45%_at_50%_25%,_rgba(255,255,255,0.10)_0%,_rgba(255,255,255,0.03)_40%,_transparent_70%)]"
        />
      </div>

      {/* Featured Products */}
      <section id="products" className="relative z-10 border-b border-white/10 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-8 py-24">
          <div className="flex items-center justify-between mb-5">
            <Typewriter
              text="PRODUCT SUITE"
              as="p"
              className="text-xs text-mono-100 tracking-[0.4em]"
              speed={30}
              enabled={heroDone}
            />
          </div>
          <Typewriter
            text="SPECIALIZED SOLUTIONS"
            as="h2"
            className="text-3xl font-bold text-mono-100 tracking-wide mb-14"
            speed={35}
            delay={400}
            enabled={heroDone}
          />

          <StaggeredFadeIn revealAfterMs={0}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            staggerDelay={100}
          >
            {featuredProducts.map((product) => {
              const IconComponent = productIcons[product.icon] || PolygonIcon;
              const accentClasses = productAccents[product.accentColor] || productAccents.cyan;
              const isAvailable = product.status === 'available' || product.status === 'beta';

              return (
                <div
                  key={product.id}
                  className="relative border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 group h-full flex flex-col"
                >
                  {product.status === 'coming_soon' && (
                    <span className="absolute top-5 right-5 text-xs text-mono-400 tracking-widest">
                      COMING SOON
                    </span>
                  )}

                  <div className={`inline-flex p-4 rounded-lg border ${accentClasses} mb-5 w-fit`}>
                    <IconComponent className="h-7 w-7" />
                  </div>

                  <h3 className="text-xl font-bold text-mono-100 tracking-wider mb-2">
                    {product.name}
                  </h3>
                  <p className={`text-sm tracking-wide mb-4 ${accentClasses.split(' ')[0]}`}>
                    {product.tagline}
                  </p>
                  <p className="text-sm text-mono-300 leading-relaxed mb-8 flex-grow">
                    {product.description.slice(0, 150)}...
                  </p>

                  <div className="mt-auto">
                    {isAvailable ? (
                      <Link
                        to={`/products/${product.slug}`}
                        className="flex items-center gap-2 text-sm text-mono-100 tracking-widest hover:text-cyan-300 transition-colors"
                      >
                        LEARN MORE
                        <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ) : (
                      <span className="text-sm text-mono-500 tracking-widest">
                        NOTIFY ME
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </StaggeredFadeIn>
        </div>
      </section>

      {/* Agentic Solutions */}
      <section className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-24">
          <Typewriter
            text="YOUR ASSISTANT"
            as="p"
            className="text-xs text-mono-100 tracking-[0.4em] mb-5"
            speed={30}
            enabled={heroDone}
          />
          <Typewriter
            text="AGENTIC SOLUTIONS"
            as="h2"
            className="text-3xl font-bold text-mono-100 tracking-wide mb-14"
            speed={35}
            delay={300}
            enabled={heroDone}
          />

          <StaggeredFadeIn revealAfterMs={0}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
            staggerDelay={100}
          >
            {/* Terra */}
            <Link
              to="/agents/terra"
              className="relative border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 group h-full flex flex-col"
            >
              <div className="inline-flex p-4 rounded-lg border text-cyan-400 bg-cyan-500/10 border-cyan-500/30 mb-5 w-fit">
                <SparklesIcon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-mono-100 tracking-wider mb-2">TERRA</h3>
              <p className="text-sm tracking-wide mb-4 text-cyan-400">
                Technical Environmental Research and Reasoning Agent
              </p>
              <p className="text-sm text-mono-300 leading-relaxed mb-8 flex-grow">
                A senior environmental scientist on demand &mdash; not a chatbot, not an API call.
                Trained across decades of remediation work, regulatory codes, and lab data, TERRA
                reasons across your full Polygon and Data Vault corpus the way an experienced
                consultant would &mdash; in plain English.
              </p>
              <div className="mt-auto">
                <span className="flex items-center gap-2 text-sm text-mono-100 tracking-widest">
                  LEARN MORE
                  <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>

            {/* CARI */}
            <Link
              to="/agents/cari"
              className="relative border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 group h-full flex flex-col"
            >
              <div className="inline-flex p-4 rounded-lg border text-cyan-400 bg-cyan-500/10 border-cyan-500/30 mb-5 w-fit">
                <BeakerIcon className="h-7 w-7" />
              </div>
              <h3 className="text-xl font-bold text-mono-100 tracking-wider mb-2">CARI</h3>
              <p className="text-sm tracking-wide mb-4 text-cyan-400">
                Comprehensive Aquifer Reasoning and Intelligence
              </p>
              <p className="text-sm text-mono-300 leading-relaxed mb-8 flex-grow">
                A senior hydrogeologist on demand &mdash; not a chatbot, not an API call. Trained
                on the full body of groundwater science &mdash; well inventories, drawdown
                histories, recharge records, TCEQ and GCD case files &mdash; CARI answers questions
                about availability, drawdown risk, and lending eligibility in plain English.
              </p>
              <div className="mt-auto">
                <span className="flex items-center gap-2 text-sm text-mono-100 tracking-widest">
                  LEARN MORE
                  <ArrowRightIcon className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          </StaggeredFadeIn>
        </div>
      </section>

      {/* Core Capabilities */}
      <section className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-24">
          <Typewriter
            text="PLATFORM"
            as="p"
            className="text-xs text-mono-100 tracking-[0.4em] mb-5"
            speed={30}
            enabled={heroDone}
          />
          <Typewriter
            text="CORE CAPABILITIES"
            as="h2"
            className="text-3xl font-bold text-mono-100 tracking-wide mb-14"
            speed={35}
            delay={300}
            enabled={heroDone}
          />
          <StaggeredFadeIn revealAfterMs={0}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            staggerDelay={80}
          >
            {coreCapabilities.map((capability) => (
              <div
                key={capability.title}
                className="rounded-lg border border-white/10 bg-white/[0.05] backdrop-blur-md p-6 h-full flex flex-col shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300"
              >
                <capability.icon className="h-6 w-6 text-cyan-400 mb-4" />
                <h3 className="text-xs font-medium text-mono-200 tracking-widest mb-3">
                  {capability.title}
                </h3>
                <p className="text-sm text-mono-400 leading-relaxed flex-grow">
                  {capability.description}
                </p>
              </div>
            ))}
          </StaggeredFadeIn>
        </div>
      </section>

      {/* Industries */}
      <section className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-24">
          <Typewriter
            text="INDUSTRIES"
            as="p"
            className="text-xs text-mono-100 tracking-[0.4em] mb-5"
            speed={30}
            enabled={heroDone}
          />
          <Typewriter
            text="WHO WE SERVE"
            as="h2"
            className="text-3xl font-bold text-mono-100 tracking-wide mb-14"
            speed={35}
            delay={300}
            enabled={heroDone}
          />
          <StaggeredFadeIn revealAfterMs={0}
            className="flex flex-col gap-5"
            staggerDelay={80}
          >
            {industries.map((industry) => (
              <div
                key={industry.name}
                className="flex items-center"
              >
                <div className="flex items-center gap-4 w-72 shrink-0">
                  <industry.icon className="h-6 w-6 text-mono-100 shrink-0" />
                  <h3 className="text-sm font-medium text-mono-100 tracking-widest whitespace-nowrap">
                    {industry.name}
                  </h3>
                </div>
                <span className="text-mono-500 shrink-0 mx-5">&#x2014;</span>
                <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg px-5 py-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_24px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 flex-1">
                  <p className="text-sm text-mono-400 leading-relaxed">
                    {industry.description}
                  </p>
                </div>
              </div>
            ))}
          </StaggeredFadeIn>
        </div>
      </section>

      {/* Who We Are — hidden from the landing page (kept in code; flip to true to restore). */}
      {false && (
      <section className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-24">
          <Typewriter
            text="ABOUT"
            as="p"
            className="text-xs text-mono-100 tracking-[0.4em] mb-5"
            speed={30}
            enabled={heroDone}
          />
          <Typewriter
            text="WHO WE ARE"
            as="h2"
            className="text-3xl font-bold text-mono-100 tracking-wide mb-8"
            speed={35}
            delay={200}
            enabled={heroDone}
          />
          <FadeIn revealAfterMs={0} delay={120} duration={500}>
            <div className="mb-14">
              <a
                href="https://talonlpe.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 transition-opacity"
              >
                <img
                  src="/talon-lpe-grey.png"
                  alt="Talon/LPE Logo"
                  className="h-20 object-contain"
                />
              </a>
            </div>
          </FadeIn>
          <FadeIn revealAfterMs={0} delay={150} duration={500}>
            <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 mb-8">
              <h3 className="text-sm font-medium text-mono-100 tracking-widest mb-4">
                THE TECHNOLOGY DIVISION OF TALON/LPE
              </h3>
              <p className="text-sm text-mono-300 leading-relaxed">
                ARGOS is the software and technology arm of Talon/LPE, one of America's leading environmental consulting and engineering firms. While Talon/LPE delivers boots-on-the-ground environmental services - drilling, remediation, compliance management - ARGOS transforms that 25+ years of field expertise into predictive tools. Every algorithm we build is informed by real-world data from thousands of contaminated sites, giving our clients technology that understands the nuances of environmental work because it was built by environmental professionals.
              </p>
            </div>
          </FadeIn>
          <StaggeredFadeIn revealAfterMs={0}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            staggerDelay={100}
          >
            <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 h-full">
              <ClockIcon className="h-7 w-7 text-mono-100 mb-5" />
              <h3 className="text-sm font-medium text-mono-100 tracking-widest mb-4">
                25+ YEARS EXPERIENCE
              </h3>
              <p className="text-sm text-mono-300 leading-relaxed">
                Operating since 1997 with proven methodologies refined through thousands of successful remediation projects.
              </p>
            </div>
            <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 h-full">
              <MapIcon className="h-7 w-7 text-mono-100 mb-5" />
              <h3 className="text-sm font-medium text-mono-100 tracking-widest mb-4">
                2,500+ SITES MANAGED
              </h3>
              <p className="text-sm text-mono-300 leading-relaxed">
                Groundwater contamination sites across the United States and South America with 20,000+ wells installed.
              </p>
            </div>
            <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 h-full">
              <UserGroupIcon className="h-7 w-7 text-mono-100 mb-5" />
              <h3 className="text-sm font-medium text-mono-100 tracking-widest mb-4">
                125+ EXPERTS
              </h3>
              <p className="text-sm text-mono-300 leading-relaxed">
                Environmental scientists, geologists, engineers, and data specialists with decades of field experience.
              </p>
            </div>
            <div className="border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg p-8 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300 h-full">
              <TrophyIcon className="h-7 w-7 text-mono-100 mb-5" />
              <h3 className="text-sm font-medium text-mono-100 tracking-widest mb-4">
                TOP 200 ENR FIRM
              </h3>
              <p className="text-sm text-mono-300 leading-relaxed">
                Recognized by Engineering News-Record. 80% repeat clients - many with us since day one.
              </p>
            </div>
          </StaggeredFadeIn>
        </div>
      </section>
      )}

      {/* CTA */}
      <section className="relative z-10 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-8 py-24 text-center">
          <Typewriter
            text="READY TO TRANSFORM YOUR ENVIRONMENTAL OPERATIONS?"
            as="h2"
            className="text-3xl font-bold text-mono-100 tracking-wide mb-5"
            speed={25}
            enabled={heroDone}
          />
          <FadeIn revealAfterMs={0} delay={1400} duration={500}>
            <p className="text-sm text-mono-300 tracking-wide mb-10">
              CREATE YOUR ACCOUNT TO GET STARTED
            </p>
            <Link
              to="/signup"
              className="inline-block px-10 py-4 border border-white/10 bg-white/[0.05] backdrop-blur-md rounded-lg text-mono-100 text-sm tracking-widest shadow-[inset_0_1px_0_0_rgba(255,255,255,0.06)] hover:border-cyan-400/50 hover:bg-white/[0.09] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_28px_-6px_rgba(34,211,238,0.45)] transition-all duration-300"
            >
              SIGN UP
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 bg-white/[0.03] backdrop-blur-md">
        <FadeIn revealAfterMs={0} delay={0} duration={400}>
          <div className="max-w-7xl mx-auto px-8 py-10">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
              <div className="flex items-baseline gap-3">
                <span className="text-base font-bold text-mono-100 tracking-[0.3em] font-orbitron">
                  ARGOS
                </span>
                <span className="text-[10px] text-mono-500 tracking-[0.25em] font-orbitron">
                  ENVIRONMENTAL AUTOMATION
                </span>
              </div>
              <div className="flex items-center gap-8">
                <a
                  href="#products"
                  className="text-[11px] text-mono-300 tracking-[0.2em] hover:text-cyan-300 transition-colors"
                >
                  PRODUCTS
                </a>
                <Link
                  to="/signup"
                  className="text-[11px] text-mono-300 tracking-[0.2em] hover:text-cyan-300 transition-colors"
                >
                  SIGN UP
                </Link>
                <Link
                  to="/login"
                  className="text-[11px] text-mono-300 tracking-[0.2em] hover:text-cyan-300 transition-colors"
                >
                  LOGIN
                </Link>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-white/[0.06] text-center">
              <p className="text-[10px] text-mono-600 tracking-[0.2em]">
                &copy; 2026 ARGOS GEO INTELLIGENCE &mdash; ALL RIGHTS RESERVED
              </p>
            </div>
          </div>
        </FadeIn>
      </footer>
    </div>
  );
}
